/**
 * shared/whats-new.ts — the "What's New" list, in ONE place because two
 * surfaces read it: the Settings → Build card (web) and the weekly recap
 * email's short "what's new" blurb (worker, founder email 2026-10-28). The
 * web package re-exports it as web/src/lib/whatsNew.ts.
 *
 * It shows the build reference is year.month.successive, "and what's new",
 * the way JitsLab's About reads (card QR9phRSRQHNx, founder ask 2026-10-13).
 *
 * Convention (mirrors JitsLab's CHANGELOG): the first entry is ROLLING — it
 * holds only what has shipped since the last deploy. On every deploy, move
 * its items into a frozen entry below (dated that day) and reset the rolling
 * entry to []. The version string is the BUILD_ID baked at ship time; the
 * local "dev" build always shows the rolling entry.
 */

export interface WhatNewEntry {
  /** BUILD_ID this entry shipped under, or 'rolling' for the open one. */
  version: string;
  date: string;
  items: string[];
}

export const WHATS_NEW: WhatNewEntry[] = [
  {
    version: '26.10.747',
    date: '2026-10-28',
    items: [
      'Your weekly meeting brings its own note — what the concierge captured, and what happens next',
      'The vault opens the camera on a phone and the library on a desktop, and each button says which it does',
      'Kudos arrives as the postcard it was sent as, addressed to your partner by name',
      'The nav lights up the same way whichever door you hover, Explore included',
      'Invite page: the Pear wordmark takes you back to pearconnected.io',
      'Type is one family again — the serif accents no longer fall back to a different face',
    ],
  },
  {
    version: 'rolling',
    date: '2026-11-03',
    items: [],
  },
  {
    version: '26.10.600',
    date: '2026-10-23',
    items: [
      'Notebook on a desktop folds its rail down to icons, so the cards get the width',
      'Every notebook card shows who can see it, right on its face',
      'Adding a card starts from a capture bar that knows what you are typing',
      'A notebook card has its own detail: the day and the place next to the note',
      'Three notebook cards sit on one line on a wide screen, each with a single date line',
      'The invite reads one sentence per line and signs off Kind Regards',
      'Say the hour out loud — "nine in the morning" now lands at nine, not seven in the evening',
    ],
  },
  {
    version: '26.10.571',
    date: '2026-10-22',
    items: [
      'Notebook on a desktop is a workspace: your collections on the left, the feed in the middle, and the card you pick opens in a panel on the right',
      'A notebook card never shows for your partner until you choose to share it, and the panel says who can see it',
      'Your notebook switches between list and grid, and remembers which you prefer',
      'Notebook cards count themselves honestly, show one date line, and no longer clip the last card at the bottom',
      'Removing a card acts straight away and offers Undo, instead of stopping you with a confirm box',
      'The More menu slides in from the same side as the navigation, and its rows are easier to hit',
      'One font stack across both canvases, so headings and body read as the same app',
      'Pear has a new app icon — the pear on the table',
      'Text across the app is a step clearer: nothing below 13px, and every control is at least 44px tall',
    ],
  },
  {
    version: '26.10.548',
    date: '2026-10-21',
    items: [
      'Gifts has its own home: say who it is for, describe them in a line, and get three picks with prices — no need to wait for an occasion',
      'A gift brief is secret by default, so you can plan for your partner without them seeing the search',
      'Every gift pick now leads with its photo, and Buy and Save sit right on the card',
      'Edit a saved note or link in place instead of deleting it and starting again, and gift links are added right where gifts live',
    ],
  },
  {
    version: '26.10.542',
    date: '2026-10-21',
    items: [
      'Gifts: Buy and Save sit inside the pick card, so the two things you actually do are reachable without scrolling',
      'Gifts: the recommendation is one pick and one sentence — the reason it fits, with its price — instead of a list of near-misses',
      'A gift idea you save stays yours until you choose to show your partner',
      'A question neither of you answered is not thrown away — it comes back about three months later',
      'The daily question reads as a question with an answer pill, and the send button no longer hides under the menu bar',
      'Pear has moved to app.pearconnected.io — the old address hands you over to the new one',
    ],
  },
  {
    version: '26.10.531',
    date: '2026-10-21',
    items: [
      'There is now an invite page for friends waiting to get in — leave an email and we open the door when there is room',
      'The landing page leads with Step Inside, and a sign-in door sits at the top where you expect it',
      'Enter starts a new line instead of sending, so a long message reads like a conversation rather than a form',
      'Pear writes to you as Pear Concierge, and your shared inbox now lives at pearconnected.io',
      'Your saved links now tell you what they are about in plain words, and the digest can pick one up as a theme',
    ],
  },
  {
    version: '26.10.518',
    date: '2026-10-20',
    items: [
      'Chores can offer a handover when one of you is quietly carrying more — one card, the same sentence on both screens, and either of you can accept it',
      'It only asks about a chore that has gone quiet for weeks, and it never shows a score',
    ],
  },
  {
    version: '26.10.521',
    date: '2026-10-20',
    items: [
      'A saved link now says what it is about in plain words — "Worth a thought", "Something to plan" — instead of explaining which website it came from',
      'Notebook cards lost their chips: one verdict, plain text, and Mark done no longer repeats what the Read link already says',
      'Share Pear sits on the Plan page as copy and a link, not as another panel',
    ],
  },
  {
    version: '26.10.515',
    date: '2026-10-20',
    items: [
      'Past questions read the way the question does — each answer under the question it belongs to, with who said it, and no boxes around it',
      'The concierge switch in the thread is just its switch again — the track shows whether it is on',
      'Share Pear moved to Settings → Plan, beside your plan, with plain buttons instead of capsules',
      'Chores: the due and time fields line up with Add, and each keeps its own label',
      'Settings is a plain row in the More menu, not a raised pill',
    ],
  },
  {
    version: '26.10.509',
    date: '2026-10-20',
    items: [
      'Chores can watch themselves — one switch in Settings → Us, offered once in the Life rail. It looks for something sliding and suggests a different approach, never a scoreboard',
      'A new recurring chore now takes turns by default, so nobody has to assign it',
      'A day nobody opened the question no longer burns it — the question defers to the evening you are both there',
      'Questions keep coming: the queue now fills itself a day ahead',
      'Notebook: a link it cannot place asks you which shelf, a failed save says so plainly, and a bad tag no longer empties the list',
      'Couple history now has a restore point that has actually been tested',
    ],
  },
  {
    version: '26.10.282',
    date: '2026-10-14',
    items: [
      'Meetings are always shared — the "Just me" option is gone from the meeting sheet',
      'Plan: the typed-transcript box is gone — capture by voice or upload, and the button reads "Summarise discussion"',
      'Errors are Title Case everywhere, with the concierge tone kept',
      'Emails sign off "Kind Regards," and "Pear" on separate lines with a break between',
      'Save any message to the Notebook with a pushpin icon instead of the word "Save"',
      'Gift concierge opens by outlining how it will pick; each pick card now reads name, price, photo, description, why it was chosen, and where to order',
      'Chores: "This week" drawer — day, task, responsible, complete, filterable on any of them',
      'Build reference is now year.month.build number (e.g. 26.10.274) with this What\'s New list beside it',
    ],
  },
  {
    version: '26.10.309',
    date: '2026-10-15',
    items: [
      'Deep links land where they promise — browser Back now retraces every screen change',
      'Compose menu is a 3×3 grid of nine tiles, with Memory, Date idea and Link added',
      'The composer sits above the comments in Thread',
      'Admin is now Inbox, and messages route to one place with sticky per-sender rules',
      'Pear Plus trials get expiry reminders at 15, 7, 1 and 0 days',
      'The More menu slides in from the right, JitsLab-style',
      'Settings subtabs are pinned — You, Us, Concierge, Plan, About',
      'Help gained a full feature inventory and richer three-beat tooltips',
      'Kudos opens as a postcard instead of a clipped form row',
      'The bottom bar goes edge-to-edge on phones and stops riding the keyboard',
      'A send whose acknowledgement dies with the socket no longer renders twice',
      'Invite entry owns the first screen for a partner arriving with a code',
    ],
  },
  {
    version: '26.10.329',
    date: '2026-10-16',
    items: [
      'Invite emails now carry a 24-hour single-use link — your partner joins with one tap',
      'The pair code stays as the fallback for when you are on a call with your partner',
      'Leaving a tab and coming back keeps your place — a half-written message, the filter you picked, and where you had scrolled',
      'Every card in the Thread is the same width, so the stream reads as one column',
      'The compose menu has a visible close button',
      "Life → Plan: section headings are bold, and the button that commits sits last on both rows",
    ],
  },
  {
    version: '26.10.337',
    date: '2026-10-16',
    items: [
      'Your first arrival offers three ways to start — invite your partner, answer today\'s question, or save something to the Notebook',
      'The nine-step tour now opens only when you ask for it, from the More menu or from Help',
      'The presence header names the two of you, and Sign out sits in the More menu',
      'Typography standardises on the three families the app actually loads',
      'A weekly recap that sends is no longer reported as a failure',
      'Pear emails — verify, reset password, weekly recap — render on the Velvet Room palette',
    ],
  },
  {
    version: '26.10.341',
    date: '2026-10-16',
    items: [
      'Thread names what is still waiting on you — an unanswered question, an ask from your partner — and jumps to the card when you tap it',
      'The Thread filters do what they say: Chat is the two of you, Asks and Questions each show exactly that',
      'A button that is busy no longer fades its own label — the disabled state stays fully readable',
      'Settings holds Help and the tour; the More menu lists destinations only',
      'A kudos from your partner celebrates once, when it arrives — and shows a quiet line instead of the burst when you prefer less motion',
      'Reveal history lives in Us, Questions — "See every reveal" opens the full dated list, and the duplicate More-menu screen is gone',
      'Inbox routing rules have one home: Life, Inbox',
    ],
  },
  {
    version: '26.10.351',
    date: '2026-10-16',
    items: [
      'Chores: react with a glyph and leave a short note on the row you ticked off',
      'Text boxes and buttons are bigger — 16px entries and 34px pills in a 44px row',
      'Your free trial now starts when your partner joins, not when you send the invite',
    ],
  },
  {
    version: '26.10.498',
    date: '2026-10-20',
    items: [
      'Some daily questions now come as a choice — tap the one that fits, and add a note if you want',
      'Rating questions arrive as 1 to 10 instead of a blank box',
      'Your answer shows as answered the moment you send it, not on the next reload',
    ],
  },
  {
    version: '26.10.496',
    date: '2026-10-20',
    items: [
      'Your daily question now comes from a bank of 300 — no repeats for months',
      'Not feeling today\'s question? Skip it and another arrives straight away, twice a day',
      'Tap "Not for us" on a question you never want to see the theme again',
    ],
  },
  {
    version: '26.10.489',
    date: '2026-10-20',
    items: [
      'The More menu lists the real screens: Today\'s question, Games, Date Plans and Partner digest under Us; For review, Chores & tasks and Weekly sync under Life; every Notebook collection as its own row',
      'Settings is a plain row in that menu now, with Sign out beneath it',
      'Question reveals read as answers indented under the question, not in cards',
      'Your daily question now cycles through everything you have not been asked before it repeats any',
      'Quick add resets itself after each entry — the caret is back in the field, and the redundant Done button is gone',
    ],
  },
  {
    version: '26.10.477',
    date: '2026-10-19',
    items: [
      'Guess Me arrives: one of you answers a question about yourself and the other guesses, with both of you seeing the score at the same time',
      'Every answer you give lands in your notebook, and each of you builds a knowing score for the topic',
      'A Guess Me round that stalls after you both lock in now reveals itself — and offers to finish if it hangs',
      'What you answer in Guess Me is encrypted at rest, like the rest of what you share',
      'Guess Me keeps what you type if the save does not land — the draft only clears once it is locked in',
      'You choose the topic for every round now, and the next-round button says which one you are starting',
      'If the assistant cannot score your typed guess, a word-for-word compare does instead — and you can still correct that verdict',
    ],
  },
  {
    version: '26.10.464',
    date: '2026-10-19',
    items: [
      'Would You Rather arrives in three modes — Silly, Spicy and Deep — with a recommendation on Today and a closing card after five turns',
      'Your words sit on white and your partner\'s on gold, and the newest message now sits at the top of Thread',
      'Notebook notes credit whoever saved them, with the date and time',
      'A saved link keeps the words you typed around it, so the note reads the way you meant it',
      'The Watch, Listen or Read control on a link speaks the same language as the labels below it',
    ],
  },
  {
    version: '26.10.457',
    date: '2026-10-19',
    items: [
      'Date preferences: each factor is now Desirable, Neutral or Undesirable — open it from the More menu',
      'One writing box across the Thread, your answers, requests and quick add',
      'On a phone the tab bar stacks its icon over its label, so every tab name shows in full',
      'Leaving explains what happens to your data, and your partner keeps one honest line in the thread',
      'The concierge switch on the composer says "Concierge on" or "Concierge off"',
      'Questions no longer offers to save an answer to the Notebook — the answer stays private to the question',
      'Notebook link cards lead with Watch, Listen or Read, and the done button says the same thing',
      'The Notebook link filters sit in one row, with search at the end',
    ],
  },
  {
    version: '26.10.421',
    date: '2026-10-18',
    items: [
      'Add your promo code when you create your account',
      'Save any answer as a private note in the Notebook',
      'Leaving a couple closes the open requests they sent you',
      'The More menu headings use the selector underline, matching the rows beneath them',
    ],
  },
  {
    version: '26.10.411',
    date: '2026-10-18',
    items: [
      'Typing into quick add no longer opens an empty strip between the composer and the keyboard',
      'Referral days are banked — a paying couple keeps the days they earned until their own cover ends',
    ],
  },
  {
    version: '26.10.393',
    date: '2026-10-17',
    items: [
      'Long answers stay inside their card instead of spilling past its edge',
      'Reveal history reads newest first — and says so',
      'A one-sided reveal names who actually answered, and a reveal with no answers no longer appears at all',
      "Today's date sits at the top right beside your names, and rolls over when the day does",
      'Game and Dates open on real headings; the weekly meeting is reachable from Life and from the More menu',
      'Your ask and chore boxes line up in their columns instead of staggering',
      'No sideways scroll on any phone width',
      'The duplicate "Needs attention" strip is gone from Thread',
    ],
  },
  {
    version: '26.10.391',
    date: '2026-10-17',
    items: [
      'Long answers stay inside their card instead of spilling past its edge',
      'Reveal history reads newest first — and says so',
      'A one-sided reveal names who actually answered, and a reveal with no answers no longer appears at all',
      "Today's date sits at the top right beside your names, and rolls over when the day does",
      'Game and Dates open on real headings; the weekly meeting is reachable from Life and from the More menu',
      'Your ask and chore boxes line up in their columns instead of staggering',
      'No sideways scroll on any phone width',
      'The duplicate "Needs attention" strip is gone from Thread',
    ],
  },
  {
    version: '26.10.388',
    date: '2026-10-17',
    items: [
      'Dark mode no longer inverts the Thread into white text on pink — the palette stays the one you chose',
      'The composer sits at the bottom of the Thread, grows as you type, and holds the Ask Pear switch',
    ],
  },
  {
    version: '26.10.384',
    date: '2026-10-17',
    items: [
      'Saved links file themselves: Pear reads the address, then the page, then the words — and when it is not sure it asks instead of guessing',
      'A gift or product link stays yours by default — your partner sees it only when you say so',
      'The gifting hint and Remove read cleanly on light backgrounds',
      "Settings says when your next weekly recap sends",
    ],
  },
  {
    version: '26.10.371',
    date: '2026-10-17',
    items: [
      'Saved links are an index now: kind chips along the top, subtype tabs under them, Saved / Planned / Done, and a search box',
      'Any link can be flagged a gift idea as you paste it — it files under Gift ideas and stays off your partner\'s list unless you say otherwise',
      'Every paste answers with a receipt: where it filed, who can see it, one tap to change that, and Undo',
      'The question reveal opens 24 hours after the question lands, and a late answer still counts',
      'Google sign-in no longer needs a page refresh to appear',
    ],
  },
];
